import React, {useContext, useEffect, useState} from 'react';
import {Card} from "../../../../../../components/card/card.jsx";
import SentryRow from "../../../../../../components/template/sentry-row.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import {MaskDefault} from "../../../../../../utils/mask.js";
import {IndividualContext} from "../../individual-context.jsx";
import {FaTriangleExclamation} from "react-icons/fa6";
import ValidateCpf from "../../../../../../components/validation/validate-cpf.jsx";
import {getAssetPath} from "../../../../../../utils/get-asset-path.js";
import Button from "../../../../../../components/button/button.jsx"
import InputDatetime from "../../../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import optionsUtils from "../../../../../../utils/options-utils.js";
import {getCities, getCountries} from "../../../../../../utils/location-utils.js";
import InputCheckbox from "../../../../../../components/input/input-checkbox.jsx";
import {differenceInYears, format} from "date-fns";
import projectUtils from "../../../../../../utils/project-utils.js";
import individualService from "../../../../../../services/web/individual-service.js";
import {v4 as uuidv4} from "uuid";
import AlertMessages from "../../../../../../components/messages/alert-message.js";
import InputSwitch from "../../../../../../components/input/input-switch.jsx";

const General = () => {
    const {individual, setIndividual, permissions, setChanges, isCpfValid, setIsCpfValid} = useContext(IndividualContext)
    const [age, setAge] = useState(null)

    useEffect(() => {
        let newAge = null
        if (individual.dtBirth) {
            newAge = differenceInYears(new Date(), individual.dtBirth)
        }

        setAge(newAge)
    }, [individual.dtBirth]);

    const handleChanges = (keys = ["general"]) => {
        setChanges(prev => {
            const changeKeys = Array.isArray(keys) ? keys : [keys];
            const nextKeys = changeKeys.filter(key => !prev.includes(key));

            return nextKeys.length ? [...prev, ...nextKeys] : prev;
        })
    }

    const onChange = ({target: {id, value}}) => {
        setIndividual(prev => ({...prev, [id]: value}));

        handleChanges()
    }

    const onChangeCustom = (value, id) => {
        setIndividual(prev => ({...prev, [id]: value}))
        handleChanges()
    }

    const onChangeCheckbox = (id, checked) => {
        setIndividual(prev => ({...prev, [id]: checked}))
        handleChanges()
    }

    const handleDate = (value, id) => {
        const dateFormated = value ? format(value, "yyyy-MM-dd") : null
        if (individual[id] === dateFormated) return

        setIndividual(prev => ({...prev, [id]: dateFormated}))
        handleChanges();
    }

    const onChangeCpf = async ({target: {value, id}}, isBlur = false) => {
        const regexCpf = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
        if (regexCpf.test(value) || isBlur) {
            ValidateCpf(value, setIsCpfValid);
        }

        setIndividual(prev => ({...prev, [id]: value}))
        handleChanges()
    }

    const searchOnCortex = () => {
        if (!permissions.cortexPermission) return;

        if (!individual.cpf) {
            projectUtils.swalMessage("warning", "", "Informe o CPF para realizar a pesquisa.", "Ok");
            return
        }

        individualService.searchOnCortex(individual.cpf)
            .then(data => {
                const importedAddresses = Array.isArray(data.address)
                    ? data.address.map(address => ({...address, itemId: uuidv4()}))
                    : [];

                setIndividual(prev => ({
                    ...prev,
                    rg: data.rg,
                    emitterRg: data.emitterRg,
                    cnh: data.cnh,
                    name: data.name,
                    socialName: data.socialName,
                    dtBirth: data.dtBirth,
                    sex: data.sex,
                    cityOfBirth: data.cityOfBirth,
                    mother: data.mother,
                    father: data.father,
                    homeless: data.homeless,
                    addresses: [...(prev.addresses ?? []), ...importedAddresses]
                }))

                handleChanges(importedAddresses.length ? ["general", "addresses"] : ["general"])
            })
            .catch(error => {
                console.error(error);

                const {message} = error.response.data;

                const errMessage = AlertMessages[message] ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
    }

    return (
        <Card>
            <Card.Body>
                <SentryRow>
                    <div className="col-span-3">
                        <div className={"flex gap-2"}>
                            <div className={"w-full relative"}>
                                <InputForm
                                    classNameWrapper={"w-full"}
                                    id="cpf"
                                    label="CPF *"
                                    value={individual.cpf}
                                    onChange={onChangeCpf}
                                    onBlur={(e) => onChangeCpf(e, true)}
                                    mask={MaskDefault.cpf}
                                    inputClass={isCpfValid ? "" : "!text-[#dc4c64] !border-[#dc4c64]"}
                                    labelClass={isCpfValid ? "" : "!text-[#dc4c64]"}
                                />

                                {
                                    !isCpfValid &&
                                    <>
                                        <small className={"text-[#dc4c64] absolute -top-2.75 right-0 text-[9px]"}>CPF inválido</small>
                                        <FaTriangleExclamation color={"#dc4c64"} className={"absolute top-2.25 right-2.5"} />
                                    </>
                                }
                            </div>
                            <Button onClick={searchOnCortex} className={"w-9 p-1!"} target={"primary-outline"} title="Pesquisar no Córtex" disabled={!permissions.cortexPermission}>
                                {permissions.cortexPermission && <img className={"w-full"} src={getAssetPath("/img/cortex-enabled.svg")} alt=""/>}
                                {!permissions.cortexPermission && <img className={"w-full"} src={getAssetPath("/img/cortex-disabled.svg")} alt=""/>}
                            </Button>
                        </div>
                    </div>
                    <div className="col-span-2">
                        <InputForm
                            id="rg"
                            label="RG"
                            value={individual.rg}
                            onChange={onChange}
                        />
                    </div>
                    <div className="col-span-2">
                        <InputForm
                            id="emitterRg"
                            label="Emissor"
                            value={individual.emitterRg}
                            onChange={onChange}
                        />
                    </div>
                    <div className="col-span-2">
                        <InputForm
                            id="criminalRg"
                            label="RG criminal"
                            value={individual.criminalRg}
                            onChange={onChange}
                        />
                    </div>

                    <div className="col-span-3">
                        <InputForm
                            id="cnh"
                            label="CNH"
                            value={individual.cnh}
                            onChange={onChange}
                        />
                    </div>
                    <div className="col-span-5">
                        <InputForm
                            id="name"
                            label="Nome *"
                            value={individual.name}
                            onChange={onChange}
                        />
                    </div>
                    <div className="col-span-4">
                        <InputForm
                            id="socialName"
                            label="Nome social"
                            value={individual.socialName}
                            onChange={onChange}
                        />
                    </div>
                    <div className="col-span-3">
                        <InputForm
                            id="nickname"
                            label="Apelido"
                            value={individual.nickname}
                            onChange={onChange}
                        />
                    </div>

                    <div className="col-span-2 relative">
                        <InputDatetime
                            id="dtBirth"
                            label="Data de nascimento"
                            selected={individual.dtBirth}
                            onChange={(date) => handleDate(date, "dtBirth")}
                            classNameWrapper={"w-full"}
                            dateFormat="L"
                        />

                        {
                            (age !== null && age > 0) &&
                            <span className={"absolute -top-2.25 right-0.5 z-[20] bg-[#2f4050] text-white rounded px-1.25 py-0.45 text-[10px]"}>{age} anos</span>
                        }
                    </div>
                    <div className="col-span-2">
                        <SelectComponent
                            id={"sex"}
                            value={individual.sex}
                            options={optionsUtils.sex2}
                            label={"Sexo"}
                            onChange={(value) => onChangeCustom(value?.value, "sex")}
                        />
                    </div>
                    <div className="col-span-2">
                        <SelectComponent
                            id={"maritalStatus"}
                            value={individual.maritalStatus}
                            options={optionsUtils.maritalStatus}
                            label={"Estado civil"}
                            onChange={(value) => onChangeCustom(value?.value, "maritalStatus")}
                        />
                    </div>
                    <div className="col-span-3">
                        <SelectComponent
                            id={`nationality`}
                            label="Nacionalidade"
                            classNameWrapper="flex-2"
                            isAsync={true}
                            loadOptions={getCountries}
                            defaultOptions={[]}
                            value={optionsUtils.getValue(individual.nationality)}
                            onChange={(value) => onChangeCustom(value?.value, "nationality")}
                        />
                    </div>
                    <div className="col-span-3">
                        <SelectComponent
                            classNameWrapper={"col-span-3"}
                            loadOptions={getCities}
                            value={optionsUtils.getValue(individual.cityOfBirth)}
                            isAsync={true}
                            label="Naturalidade"
                            onChange={(value) => onChangeCustom(value?.value, "cityOfBirth")}
                            cacheOptions
                            defaultOptions={[]}
                        />
                    </div>


                    <div className="col-span-5">
                        <InputForm
                            id="mother"
                            label="Nome da mãe"
                            value={individual.mother}
                            onChange={onChange}
                        />
                    </div>
                    <div className="col-span-7">
                        <InputForm
                            classNameWrapper={"w-full"}
                            id="father"
                            label="Nome do pai"
                            value={individual.father}
                            onChange={onChange}
                        />
                    </div>
                    <div className="col-span-12 flex gap-4">
                        <InputSwitch
                            className={"w-[200px]"}
                            id={"mentalDisorder"}
                            label={"Possui transtorno mental"}
                            checked={!!individual.mentalDisorder}
                            onChange={(checked) => onChangeCheckbox("mentalDisorder", checked)}
                        />
                        <InputSwitch
                            className={"w-[210px]"}
                            id={"alcoholDrugDependency"}
                            label={"Dependência álcool/drogas"}
                            checked={!!individual.alcoholDrugDependency}
                            onChange={(checked) => onChangeCheckbox("alcoholDrugDependency", checked)}
                        />
                    </div>
                </SentryRow>
            </Card.Body>
        </Card>
    );
};

export default General;
